import { onReady } from '../utils/dom';

type ActionTone = 'primary' | 'success' | 'info' | 'warning' | 'danger' | 'neutral';

const ACTION_TERMS: Record<ActionTone, string[]> = {
  primary: [
    'aggiungi',
    'nuovo',
    'crea',
    'inserisci',
    'preventivo',
    'ordine',
    'carrello',
    'accedi',
    'entra',
    'login'
  ],
  success: ['salva', 'conferma', 'approva', 'invia', 'pubblica', 'aggiorna', 'carica', 'registra'],
  info: ['scarica', 'download', 'pdf', 'dettaglio', 'visualizza', 'apri', 'vedi', 'esporta', 'stampa'],
  warning: ['modifica', 'gestisci', 'cambia', 'sconto', 'varia', 'ripristina'],
  danger: ['elimina', 'rimuovi', 'cancella', 'svuota', 'annulla ordine', 'annulla preventivo'],
  neutral: ['chiudi', 'annulla', 'indietro', 'reset', 'filtra', 'pulisci']
};

const SEARCH_TERMS = [
  'cerca',
  'ricerca',
  'search',
  'trova',
  'prodotto',
  'prodotti',
  'cliente',
  'clienti',
  'ordine',
  'ordini',
  'preventivo',
  'preventivi',
  'codice',
  'nome'
];

function normalizeText(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function getControlText(element: Element): string {
  if (element instanceof HTMLInputElement) return element.value || element.getAttribute('aria-label') || '';

  return [
    element.textContent,
    element.getAttribute('aria-label'),
    element.getAttribute('title'),
    element.getAttribute('data-label')
  ]
    .filter(Boolean)
    .join(' ');
}

function hasVisibleText(element: Element): boolean {
  if (element instanceof HTMLInputElement) return Boolean(element.value?.trim());
  return normalizeText(element.textContent).length > 1;
}

function detectActionTone(text: string): ActionTone {
  const normalized = normalizeText(text);

  const order: ActionTone[] = ['danger', 'success', 'warning', 'info', 'neutral', 'primary'];
  for (const tone of order) {
    if (ACTION_TERMS[tone].some((term) => normalized.includes(term))) return tone;
  }

  return 'neutral';
}


function looksLikeSelectControl(element: HTMLElement): boolean {
  const role = normalizeText(element.getAttribute('role'));
  const ariaHasPopup = normalizeText(element.getAttribute('aria-haspopup'));
  const text = normalizeText(getControlText(element));

  return (
    role === 'combobox' ||
    ariaHasPopup === 'listbox' ||
    text.includes('tutte le categorie') ||
    text.includes('tutte le aziende') ||
    text.includes('nome a-z') ||
    text.includes('prezzo') ||
    text.includes('categoria') ||
    text.includes('azienda')
  );
}

function enhanceSelectControls(root: ParentNode = document): void {
  const controls = root.querySelectorAll<HTMLElement>(
    'select, button[role="combobox"], [aria-haspopup="listbox"]'
  );

  for (const control of controls) {
    control.classList.add('im-ux-control', 'im-ux-select-control');
    control.dataset.imUxLock = '1';
  }
}

function enhanceActionControls(root: ParentNode = document): void {
  const controls = root.querySelectorAll<HTMLElement>(
    'button, a[role="button"], input[type="button"], input[type="submit"], input[type="reset"]'
  );

  for (const control of controls) {
    if (control.id === 'backToTop') continue;
    if (control.closest('.iubenda-embed')) continue;

    if (looksLikeSelectControl(control)) {
      control.classList.add('im-ux-control', 'im-ux-select-control');
      control.dataset.imUxLock = '1';
      continue;
    }

    if (control.dataset.imUxLock === '1') continue;

    const text = getControlText(control);
    const visibleText = hasVisibleText(control);
    const tone = detectActionTone(text);

    control.classList.add('im-ux-control');
    control.dataset.imUxTone = tone;

    if (!visibleText) {
      control.classList.add('im-ux-icon-control');
      continue;
    }

    control.classList.add('im-ux-action-control');
  }
}

function looksLikeSearchInput(input: HTMLInputElement): boolean {
  const type = normalizeText(input.type || 'text');
  if (!['text', 'search', 'email', ''].includes(type)) return false;

  const haystack = normalizeText(
    [input.placeholder, input.name, input.id, input.getAttribute('aria-label'), input.getAttribute('data-label')]
      .filter(Boolean)
      .join(' ')
  );

  return SEARCH_TERMS.some((term) => haystack.includes(term));
}

function updateValueState(input: HTMLInputElement): void {
  input.dataset.imUxHasValue = input.value.trim() ? '1' : '0';
}

function enhanceSearchFields(root: ParentNode = document): void {
  const inputs = root.querySelectorAll<HTMLInputElement>('input');

  for (const input of inputs) {
    if (!looksLikeSearchInput(input)) continue;

    input.classList.add('im-ux-search-field');
    input.setAttribute('autocomplete', input.getAttribute('autocomplete') || 'off');

    const shell = input.closest<HTMLElement>('label, div, form');
    if (shell && !shell.classList.contains('im-ux-search-shell')) {
      shell.classList.add('im-ux-search-shell');
    }

    updateValueState(input);
    if (input.dataset.imUxInputBound === '1') continue;

    input.dataset.imUxInputBound = '1';
    input.addEventListener('input', () => updateValueState(input), { passive: true });
    input.addEventListener('change', () => updateValueState(input), { passive: true });
  }
}

function enhanceInterface(root: ParentNode = document): void {
  document.documentElement.classList.add('im-ux-enabled');
  enhanceSelectControls(root);
  enhanceActionControls(root);
  enhanceSearchFields(root);
}

export function initInterfaceEnhancements(): void {
  onReady(() => {
    enhanceInterface();

    let scheduled = false;
    const scheduleEnhancement = () => {
      if (scheduled) return;
      scheduled = true;

      window.requestAnimationFrame(() => {
        scheduled = false;
        enhanceInterface();
      });
    };

    const observer = new MutationObserver(scheduleEnhancement);
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('im:legacy-loaded', scheduleEnhancement);
  });
}
